import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { api, type SeoFileReport } from "@/lib/api";
import { useTranslations } from "@/hooks/use-translations";
import { useAuth } from "@/hooks/use-auth";
import { userFacingMessage } from "@/lib/user-facing-message";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SafeReportMarkdown } from "./safe-report-markdown";
import { formatFileSize } from "./file-preview";

function Scope({ label, value }: { label: string; value: Record<string, unknown> | null }) {
	const t = useTranslations("fileReports");
	return <div><dt className="text-text-tertiary">{label}</dt><dd className="text-text-secondary break-words">
		{value ? Object.entries(value).map(([key, entry]) => `${key}: ${typeof entry === "object" ? JSON.stringify(entry) : String(entry)}`).join(" · ") : t("scopeUnknown")}
	</dd></div>;
}

function Coverage({ value }: { value: Record<string, unknown> }) {
	const t = useTranslations("fileReports");
	const number = (key: string) => typeof value[key] === "number" ? value[key] as number : null;
	const gsc = value.gsc as Record<string, unknown> | undefined;
	return <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-text-secondary">
		{number("captured_answers") !== null && <span>{t("answers")}: {number("captured_answers")}/{number("planned") ?? "—"}</span>}
		{number("unclassified_answers") !== null && <span>{t("unclassified")}: {number("unclassified_answers")}</span>}
		{["not_run", "blocked", "errors", "missing", "no_ai_answer"].map((key) => number(key) ? <span key={key}>{t(`coverage.${key}`)}: {number(key)}</span> : null)}
		{gsc && typeof gsc.ok === "number" && <span>GSC: {gsc.ok}/{typeof gsc.planned === "number" ? gsc.planned : "—"}
			{typeof gsc.no_data === "number" && gsc.no_data > 0 ? ` · ${t("noData")}: ${gsc.no_data}` : ""}</span>}
	</div>;
}

function ReportCard({ report, primary }: { report: SeoFileReport; primary: boolean }) {
	const t = useTranslations("fileReports");
	const queryClient = useQueryClient();
	const retry = useMutation({
		mutationFn: () => api.retryReportDelivery(report.analysisId, report.id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["seo-reports", report.analysisId] }),
		onError: (error) => toast.error(error instanceof Error ? error.message : t("retryError")),
	});
	const saved = report.files.filter((file) => file.deliveryState === "ready" && file.fileId);
	const md = saved.find((file) => file.filename === "report.md");
	const html = saved.find((file) => file.filename === "report.html");
	const saving = report.deliveryState === "pending" || report.deliveryState === "saving";
	return <section className="rounded-2xl border border-border-subtle bg-bg-surface p-6 md:p-8 space-y-5">
		<div className="flex flex-wrap items-center justify-between gap-3 text-sm">
			<Badge tone={report.reportStatus === "partial" ? "warning" : "success"}>{t(report.reportStatus === "partial" ? "partial" : "created")}</Badge>
			<time className="text-text-tertiary">{new Date(report.createdAt).toLocaleString()}</time>
		</div>
		{saving && <Badge tone="info"><Loader2 size={12} className="animate-spin" />{t("saving", { count: saved.length, total: report.files.length })}</Badge>}
		{report.deliveryState === "ready" && <Badge tone="success">{t("saved")}</Badge>}
		{report.deliveryState === "failed" && <div className="rounded-xl border border-warning/20 bg-warning-bg p-4 space-y-3">
			<p className="text-warning text-sm">{t("deliveryFailed")} {saved.length}/{report.files.length}</p>
			<p className="text-sm text-text-secondary">{report.lastError ? userFacingMessage(report.lastError) : null}</p>
			<Button variant="secondary" onClick={() => retry.mutate()} disabled={retry.isPending}>{t("retryDelivery")}</Button>
		</div>}
		{saving && report.lastError && <p className="text-sm text-warning">{t("retryScheduled")}</p>}
		<h2 className="text-text-primary text-lg font-semibold">{t("summary")}</h2>
		<div className="chat-markdown text-sm leading-relaxed"><SafeReportMarkdown>{report.summaryMarkdown}</SafeReportMarkdown></div>
		<Coverage value={report.coverage} />
		{report.limitations.length > 0 && <div className="rounded-xl border border-warning/20 bg-warning-bg p-4 text-sm text-warning">
			<h3 className="font-semibold mb-2">{t("limitations")}</h3><ul className="list-disc pl-5 space-y-1">{report.limitations.map((text, index) => <li key={index}>{userFacingMessage(text)}</li>)}</ul>
		</div>}
		<dl className="grid gap-3 sm:grid-cols-2 text-xs"><Scope label={t("requestedScope")} value={report.requestedScope} /><Scope label={t("measuredScope")} value={report.measuredScope} /></dl>
		<p className="text-xs text-text-tertiary">{report.validationScope === "structure_and_evidence_hashes_only" ? t("validation") : report.validationScope}</p>
		<div className="flex flex-wrap gap-3">
			{md?.fileId && <>
				<Button asChild variant={primary ? "primary" : "secondary"}><Link to={`/files/${md.fileId}`}><ExternalLink size={16} />{t("fullReport")}</Link></Button>
				<Button asChild variant="secondary"><a href={api.fileDownloadUrl(md.fileId)}><Download size={16} />Markdown</a></Button>
			</>}
			{html?.fileId && <Button asChild variant="secondary"><a href={api.fileDownloadUrl(html.fileId)}><Download size={16} />HTML</a></Button>}
		</div>
		<details className="text-sm text-text-secondary"><summary className="cursor-pointer">{t("dataFiles")}</summary>
			<ul className="mt-3 space-y-2">{saved.filter((file) => file.filename.endsWith(".json")).map((file) => <li key={file.filename} className="flex flex-wrap items-center gap-4">
				<Link to={`/files/${file.fileId}`} className="text-text-link underline underline-offset-2">{file.filename}</Link><span className="text-xs">{formatFileSize(file.sizeBytes)}</span>
				<a href={api.fileDownloadUrl(file.fileId!)} className="text-text-link underline underline-offset-2">{t("download")}</a>
			</li>)}</ul>
		</details>
	</section>;
}

export function SeoFileReports({ analysisId, processing }: { analysisId: string; processing: boolean }) {
	const { user } = useAuth();
	const t = useTranslations("fileReports");
	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["seo-reports", analysisId, user?.id], queryFn: () => api.getAnalysisReports(analysisId), enabled: !!user?.id,
		refetchInterval: (query) => processing || query.state.data?.reports.some((report) => ["pending", "saving"].includes(report.deliveryState)) ? 5000 : false,
	});
	if (isLoading) return <p className="text-text-tertiary">{t("loading")}</p>;
	if (isError) return <div className="text-text-secondary space-y-3"><p>{t("readError")}</p><Button variant="secondary" onClick={() => refetch()}>{t("retry")}</Button></div>;
	if (!data?.reports.length) return processing ? null : <p className="text-text-tertiary">{t("noReport")}</p>;
	return <div className="space-y-6">{data.reports.map((report, index) => <ReportCard key={report.id} report={report} primary={index === 0} />)}</div>;
}
